// Role utility functions for the new security system
// Date: 2025-08-12

/**
 * Get the effective role for a user, checking JWT first, then database fallback
 * @param {Object} user - The user object from Supabase auth
 * @returns {string} The user's effective role
 */
export const getUserRole = (user) => {
  if (!user) return null;
  
  // First check JWT claims (preferred method)
  if (user.app_metadata?.role) {
    return user.app_metadata.role;
  }
  
  // Fallback to database role (for backward compatibility)
  if (user.role) {
    return user.role;
  }
  
  return 'team_member'; // Default role
};

/**
 * Check if user has a specific role
 * @param {Object} user - The user object
 * @param {string} role - The role to check for
 * @returns {boolean}
 */
export const hasRole = (user, role) => {
  const userRole = getUserRole(user);
  return userRole === role;
};

/**
 * Check if user has any of the specified roles
 * @param {Object} user - The user object
 * @param {string[]} roles - Array of roles to check
 * @returns {boolean}
 */
export const hasAnyRole = (user, roles) => {
  const userRole = getUserRole(user);
  return roles.includes(userRole);
};

/**
 * Check if user is a team manager (primary or assistant)
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const isTeamManager = (user) => {
  // Check for both old and new role formats
  const validTeamManagerRoles = [
    'team_manager_primary', 
    'team_manager_assistant',
    'team_manager' // Legacy role name that might still be in use
  ];
  return hasAnyRole(user, validTeamManagerRoles);
};

/**
 * Check if user is a primary team manager
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const isPrimaryTeamManager = (user) => {
  return hasRole(user, 'team_manager_primary');
};

/**
 * Check if user is a TeamRezy employee
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const isTeamRezyEmployee = (user) => {
  return hasRole(user, 'teamrezy_employee');
};

/**
 * Check if user is a venue manager
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const isVenueManager = (user) => {
  return hasRole(user, 'venue_manager');
};

/**
 * Check if user can manage teams (create, edit, delete)
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const canManageTeams = (user) => {
  return hasAnyRole(user, ['teamrezy_employee', 'team_manager_primary', 'team_manager_assistant']);
};

/**
 * Check if user can invite team managers
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const canInviteTeamManagers = (user) => {
  return hasAnyRole(user, ['teamrezy_employee', 'team_manager_primary']);
};

/**
 * Check if user can manage venues
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const canManageVenues = (user) => {
  return hasAnyRole(user, ['teamrezy_employee', 'venue_manager']);
};

/**
 * Check if user can manage reservations
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const canManageReservations = (user) => {
  return hasAnyRole(user, ['teamrezy_employee', 'team_manager_primary', 'team_manager_assistant', 'venue_manager']);
};

/**
 * Check if user can create reservations
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const canCreateReservations = (user) => {
  return hasAnyRole(user, ['teamrezy_employee', 'team_manager_primary', 'team_manager_assistant']);
};

/**
 * Check if user can approve team manager requests
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const canApproveTeamManagers = (user) => {
  return hasAnyRole(user, ['teamrezy_employee', 'team_manager_primary']);
};

/**
 * Get navigation tabs based on user role
 * @param {Object} user - The user object
 * @returns {Array} Array of navigation tab objects
 */
export const getNavigationTabs = (user) => {
  const userRole = getUserRole(user);
  
  const tabConfigs = {
    teamrezy_employee: [
      { id: 'overview', label: 'Overview', icon: 'BarChart3' },
      { id: 'reservations', label: 'Reservations', icon: 'Calendar' },
      { id: 'venues', label: 'Venues', icon: 'MapPin' },
      { id: 'games', label: 'Games', icon: 'Trophy' },
      { id: 'tournament_games', label: 'Tournament Games', icon: 'CalendarDays' },
      { id: 'users', label: 'Users', icon: 'Users' },
      { id: 'teams', label: 'Teams', icon: 'Users' },
      { id: 'account', label: 'Account', icon: 'Settings' }
    ],
    team_manager_primary: [
      { id: 'overview', label: 'Overview', icon: 'BarChart3' },
      { id: 'teams', label: 'Teams', icon: 'Users' },
      { id: 'tournaments', label: 'Tournaments', icon: 'Trophy' },
      { id: 'reservations', label: 'Reservations', icon: 'Calendar' },
      { id: 'book', label: 'Add Reservation', icon: 'Plus' },
      { id: 'account', label: 'Account', icon: 'Settings' }
    ],
    team_manager_assistant: [
      { id: 'overview', label: 'Overview', icon: 'BarChart3' },
      { id: 'teams', label: 'Teams', icon: 'Users' },
      { id: 'tournaments', label: 'Tournaments', icon: 'Trophy' },
      { id: 'reservations', label: 'Reservations', icon: 'Calendar' },
      { id: 'book', label: 'Add Reservation', icon: 'Plus' },
      { id: 'account', label: 'Account', icon: 'Settings' }
    ],
    venue_manager: [
      { id: 'overview', label: 'Overview', icon: 'BarChart3' },
      { id: 'reservations', label: 'Reservations', icon: 'Calendar' },
      { id: 'venues', label: 'My Venue', icon: 'MapPin' },
      { id: 'account', label: 'Account', icon: 'Settings' }
    ],
    team_member: [
      { id: 'overview', label: 'Overview', icon: 'BarChart3' },
      { id: 'account', label: 'Account', icon: 'Settings' }
    ]
  };
  
  return tabConfigs[userRole] || tabConfigs.team_member;
};

/**
 * Get role display name for UI
 * @param {string} role - The role string
 * @returns {string} Human-readable role name
 */
export const getRoleDisplayName = (role) => {
  const roleNames = {
    'teamrezy_employee': 'TeamRezy Employee',
    'team_manager_primary': 'Primary Team Manager',
    'team_manager_assistant': 'Assistant Team Manager',
    'venue_manager': 'Venue Manager',
    'team_member': 'Team Member'
  };
  
  return roleNames[role] || 'Unknown Role';
};

/**
 * Get role badge CSS class for UI styling
 * @param {string} role - The role string
 * @returns {string} CSS class string
 */
export const getRoleBadgeClass = (role) => {
  const badgeClasses = {
    'teamrezy_employee': 'bg-purple-100 text-purple-800',
    'team_manager_primary': 'bg-blue-100 text-blue-800',
    'team_manager_assistant': 'bg-green-100 text-green-800',
    'venue_manager': 'bg-orange-100 text-orange-800',
    'team_member': 'bg-gray-100 text-gray-800'
  };
  
  return badgeClasses[role] || 'bg-gray-100 text-gray-800';
};

/**
 * Check if user can access a specific tab
 * @param {Object} user - The user object
 * @param {string} tabId - The tab ID to check
 * @returns {boolean}
 */
export const canAccessTab = (user, tabId) => {
  const userTabs = getNavigationTabs(user);
  return userTabs.some(tab => tab.id === tabId);
};

/**
 * Get default tab for user role
 * @param {Object} user - The user object
 * @returns {string} Default tab ID
 */
export const getDefaultTab = (user) => {
  const userTabs = getNavigationTabs(user);
  return userTabs.length > 0 ? userTabs[0].id : 'overview';
};
